import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import type { UserWithTeam } from '@shared/schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface NeedHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
  availableUsers: UserWithTeam[];
}

export default function NeedHelpModal({
  isOpen,
  onClose,
  taskId,
  taskTitle,
  availableUsers: rawAvailableUsers,
}: NeedHelpModalProps) {
  const availableUsers = useMemo(() => {
    if (!rawAvailableUsers || !Array.isArray(rawAvailableUsers)) {
      return [];
    }
    return rawAvailableUsers;
  }, [rawAvailableUsers]);
  const isLoadingUsers =
    !rawAvailableUsers || !Array.isArray(rawAvailableUsers);

  // Debug logging
  console.log('NeedHelpModal - rawAvailableUsers:', rawAvailableUsers);
  console.log('NeedHelpModal - availableUsers:', availableUsers);
  console.log('NeedHelpModal - isLoadingUsers:', isLoadingUsers);
  console.log('NeedHelpModal - availableUsers.length:', availableUsers.length);
  console.log('NeedHelpModal - availableUsers type:', typeof availableUsers);
  console.log(
    'NeedHelpModal - availableUsers is array:',
    Array.isArray(availableUsers)
  );

  const [selectedHelpers, setSelectedHelpers] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const distinctTeams = Array.from(
    new Map(
      (availableUsers || [])
        .filter(u => u.team)
        .map(u => [
          (u.team!.id as any) || u.team!.name,
          { id: (u.team as any)?.id || (u.team as any), name: u.team!.name },
        ])
    ).values()
  );
  const [teamFilter, setTeamFilter] = useState<string>('all');

  const needHelpMutation = useMutation({
    mutationFn: async ({
      helperIds,
      message,
    }: {
      helperIds: string[];
      message: string;
    }) => {
      await apiRequest('POST', `/api/tasks/${taskId}/need-help`, {
        helperIds,
        message,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      // Also invalidate staff-specific task queries so helper boards refresh
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: 'Help Request Sent',
        description: `Your help request has been sent to ${selectedHelpers.length} user${selectedHelpers.length > 1 ? 's' : ''}.`,
      });
      handleClose();
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to send help request',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    if (selectedHelpers.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one user to help',
        variant: 'destructive',
      });
      return;
    }

    needHelpMutation.mutate({
      helperIds: selectedHelpers,
      message: message.trim(),
    });
  };

  const handleClose = () => {
    setSelectedHelpers([]);
    setMessage('');
    onClose();
  };

  const toggleHelper = (userId: string) => {
    setSelectedHelpers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const removeHelper = (userId: string) => {
    setSelectedHelpers(prev => prev.filter(id => id !== userId));
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Don't render if we don't have valid data
  if (!rawAvailableUsers || !Array.isArray(rawAvailableUsers)) {
    console.log(
      'NeedHelpModal - Not rendering due to invalid data:',
      rawAvailableUsers
    );
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Need Help with Task
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-sm leading-relaxed">
            Select team members who can help you with this task and add a
            message describing what you need.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">
              Task
            </Label>
            <p className="text-sm text-gray-900 font-medium">{taskTitle}</p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Select Team Members to Help
            </Label>

            {/* Search and Filter Controls */}
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Input
                  placeholder="Search by name or team..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="h-10 pl-10 pr-4"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger
                  className="h-10 w-48"
                  aria-label="Filter by team"
                >
                  <SelectValue placeholder="All teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {distinctTeams.map(t => (
                    <SelectItem
                      key={t.id || t.name}
                      value={(t.id || t.name) as any}
                    >
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const q = search.trim().toLowerCase();
                    const users = availableUsers || [];
                    const filtered = users.filter(u => {
                      const matchesSearch =
                        !q ||
                        (u.firstName || '').toLowerCase().includes(q) ||
                        (u.lastName || '').toLowerCase().includes(q) ||
                        (u.team?.name || '').toLowerCase().includes(q);
                      const matchesTeam =
                        teamFilter === 'all' ||
                        ((u.team?.id || u.team?.name) as any) === teamFilter;
                      return matchesSearch && matchesTeam;
                    });
                    setSelectedHelpers(prev =>
                      Array.from(new Set([...prev, ...filtered.map(u => u.id)]))
                    );
                  }}
                  className="h-8 px-3 text-xs"
                >
                  Select all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedHelpers([])}
                  className="h-8 px-3 text-xs"
                >
                  Clear selected
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-700">
                  Selected {selectedHelpers.length}
                </span>
                {selectedHelpers.length > 0 && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
            </div>

            {/* Selected helpers display */}
            {selectedHelpers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedHelpers.map(helperId => {
                  const helper = (availableUsers || []).find(
                    u => u.id === helperId
                  );
                  if (!helper) return null;
                  return (
                    <div
                      key={helperId}
                      className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                    >
                      <Avatar className="w-4 h-4 mr-2">
                        <AvatarImage
                          src={helper.profileImageUrl || undefined}
                        />
                        <AvatarFallback className="text-xs">
                          {getInitials(
                            helper.firstName || undefined,
                            helper.lastName || undefined
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {helper.firstName} {helper.lastName}
                      </span>
                      <button
                        onClick={() => removeHelper(helperId)}
                        className="ml-2 hover:text-blue-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Team members list */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-sm">
              {isLoadingUsers ? (
                <div className="p-4 space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center space-x-3 p-3">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : availableUsers.length > 0 ? (
                availableUsers
                  .filter(u => {
                    const q = search.trim().toLowerCase();
                    const matchesSearch =
                      !q ||
                      (u.firstName || '').toLowerCase().includes(q) ||
                      (u.lastName || '').toLowerCase().includes(q) ||
                      (u.team?.name || '').toLowerCase().includes(q);
                    const matchesTeam =
                      teamFilter === 'all' ||
                      ((u.team?.id || u.team?.name) as any) === teamFilter;
                    return matchesSearch && matchesTeam;
                  })
                  .map(user => (
                    <div
                      key={user.id}
                      className={`flex items-center p-4 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                        selectedHelpers.includes(user.id)
                          ? 'bg-blue-50 border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleHelper(user.id)}
                    >
                      <Checkbox
                        checked={selectedHelpers.includes(user.id)}
                        onChange={() => toggleHelper(user.id)}
                        className="mr-4"
                      />
                      <Avatar className="w-10 h-10 mr-4 ring-2 ring-offset-2 ring-blue-100">
                        <AvatarImage src={user.profileImageUrl || undefined} />
                        <AvatarFallback className="text-sm font-medium bg-blue-100 text-blue-700">
                          {getInitials(
                            user.firstName || undefined,
                            user.lastName || undefined
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </div>
                        {user.team && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            {user.team.name}
                          </div>
                        )}
                      </div>
                      {selectedHelpers.includes(user.id) && (
                        <div className="ml-2 flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <div className="p-3 text-sm text-gray-500">
                  <div>No users available for help.</div>
                  <div className="text-xs mt-2">
                    Debug: availableUsers.length = {availableUsers.length},
                    isLoadingUsers = {isLoadingUsers.toString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="message"
              className="text-sm font-medium text-gray-700 flex items-center gap-2"
            >
              <svg
                className="h-4 w-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Message (Optional)
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Describe what specific help you need..."
              className="min-h-[100px] resize-none border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              data-testid="textarea-help-message"
            />
            <div className="text-xs text-gray-500 text-right">
              {message.length}/500 characters
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="button-cancel-help"
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                needHelpMutation.isPending || selectedHelpers.length === 0
              }
              data-testid="button-send-help-request"
              className="px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
            >
              {needHelpMutation.isPending ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Send Help Request
                  {selectedHelpers.length > 1
                    ? ` (${selectedHelpers.length})`
                    : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
